/**
 * CM5 Camera Activity measurement client.
 *
 * Frontend consumes frozen V1 truth only. It does not call CM4 or recompute
 * P/F/S/U/O, shares, completeness, or measurement state.
 */
import { api } from './api';

export const CAMERA_ACTIVITY_MEASUREMENT_STATES = [
  'MEASURED',
  'LIMITED',
  'NOT_MEASURED',
  'UNSUPPORTED',
] as const;

export type CameraActivityMeasurementState =
  (typeof CAMERA_ACTIVITY_MEASUREMENT_STATES)[number];

export type CameraActivityScope = 'CAMERA' | 'ZONE';

export type CameraActivityMetric = {
  state: string;
  seconds: number | null;
  share: number | null;
};

export type CameraActivityMeasurementResponse = {
  ok: boolean;
  subject: {
    site_id: string;
    device_id: string;
    camera_id: string;
    scope: string;
    zone_id: string | null;
    zone_version: number | null;
  };
  window: {
    start: string;
    end: string;
  };
  state: string;
  claim_eligible: boolean;
  reason_codes: string[];
  metrics: {
    pedestrian_presence: CameraActivityMetric;
    forklift_presence: CameraActivityMetric;
    simultaneous_presence: CameraActivityMetric;
  };
  quality: {
    usable_camera_seconds: number | null;
    source_observed_seconds: number | null;
    producer_completeness: number | null;
    producer_gap_seconds: number | null;
    coverage_complete: boolean;
    activity_complete: boolean;
    source_compatible: boolean;
  };
  provenance: {
    measurement_definition_version: string | null;
    source_signature: string | null;
    producer_version: string | null;
    zone_geometry_identity?: string | null;
  };
};

export type CameraActivityMeasurementRequest = {
  siteId: string;
  deviceId: string;
  cameraId: string;
  start: string;
  end: string;
  scope: CameraActivityScope;
  zoneId?: string | null;
  zoneVersion?: number | null;
};

export type CameraActivityMeasurementResult =
  | { kind: 'ok'; data: CameraActivityMeasurementResponse }
  | { kind: 'http'; status: number; error: string | null; detail: string | null }
  | { kind: 'network'; message: string };

function readErrorField(payload: unknown, key: string): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const value = (payload as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

export function cameraActivityMeasurementPath(args: {
  siteId: string;
  deviceId: string;
  cameraId: string;
}): string {
  return (
    `/api/v1/sites/${encodeURIComponent(args.siteId)}` +
    `/devices/${encodeURIComponent(args.deviceId)}` +
    `/cameras/${encodeURIComponent(args.cameraId)}` +
    `/activity-measurement`
  );
}

export async function getCameraActivityMeasurement(
  args: CameraActivityMeasurementRequest,
): Promise<CameraActivityMeasurementResult> {
  const params: Record<string, string> = {
    start: args.start,
    end: args.end,
    scope: args.scope,
  };
  if (args.scope === 'ZONE') {
    if (args.zoneId) params.zone_id = args.zoneId;
    if (args.zoneVersion != null) params.zone_version = String(args.zoneVersion);
  }
  try {
    const response = await api.get(
      cameraActivityMeasurementPath({
        siteId: args.siteId,
        deviceId: args.deviceId,
        cameraId: args.cameraId,
      }),
      { params },
    );
    return { kind: 'ok', data: response.data as CameraActivityMeasurementResponse };
  } catch (err) {
    const axiosLike = err as {
      message?: string;
      response?: { status?: number; data?: unknown };
    };
    const status = axiosLike.response?.status;
    if (typeof status === 'number') {
      return {
        kind: 'http',
        status,
        error: readErrorField(axiosLike.response?.data, 'error'),
        detail: readErrorField(axiosLike.response?.data, 'detail'),
      };
    }
    return { kind: 'network', message: axiosLike.message || 'network_error' };
  }
}
